/**
 * Authentication utilities - token storage and API calls
 */
import { getBackendUrl } from './backendUrl';

// Token storage keys
const ACCESS_TOKEN_KEY = 'craftmyparty_access_token';
const REFRESH_TOKEN_KEY = 'craftmyparty_refresh_token';
const USER_KEY = 'craftmyparty_user';
const BACKEND_URL_KEY = 'craftmyparty_backend_url'; // Track which backend URL tokens are for
const LOGOUT_FLAG_KEY = 'craftmyparty_logout_flag'; // Flag to signal logout across tabs
const LAST_ACTIVE_AT_KEY = 'craftmyparty_last_active_at';

export const SESSION_IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const SESSION_ACTIVITY_TOUCH_INTERVAL_MS = 30 * 1000; // 30 seconds

/**
 * Get stored access token
 */
export const getAccessToken = () => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

/**
 * Get stored refresh token
 */
export const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Store tokens and user info
 */
export const setAuthTokens = (accessToken, refreshToken, user) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  localStorage.setItem(LAST_ACTIVE_AT_KEY, Date.now().toString());
  // Store the backend URL these tokens are for
  localStorage.setItem(BACKEND_URL_KEY, getBackendUrl());
  // Clear any logout flag (in case of login after logout)
  localStorage.removeItem(LOGOUT_FLAG_KEY);
};

/**
 * Check if stored tokens are for a different backend URL
 * If so, clear them to prevent authentication issues
 */
export const validateBackendUrl = () => {
  const storedBackendUrl = localStorage.getItem(BACKEND_URL_KEY);
  const currentBackendUrl = getBackendUrl();

  // Check if we have tokens (access token exists)
  const hasTokens = !!localStorage.getItem(ACCESS_TOKEN_KEY);

  // If we have tokens but they're for a different backend, clear them
  if (storedBackendUrl && storedBackendUrl !== currentBackendUrl && hasTokens) {
    console.warn('⚠️ Backend URL changed. Clearing old authentication tokens.');
    console.warn(`Previous backend: ${storedBackendUrl}`);
    console.warn(`Current backend: ${currentBackendUrl}`);
    clearAuth();
    return false;
  }

  return true;
};

/**
 * Get stored user info
 */
export const getUser = () => {
  const userStr = localStorage.getItem(USER_KEY);
  return userStr ? JSON.parse(userStr) : null;
};

/**
 * Clear all auth data
 */
export const clearAuth = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(BACKEND_URL_KEY);
  localStorage.removeItem(LAST_ACTIVE_AT_KEY);
  // Set logout flag to signal other tabs
  localStorage.setItem(LOGOUT_FLAG_KEY, Date.now().toString());
  // Remove the flag after a short delay to allow other tabs to detect it
  setTimeout(() => {
    localStorage.removeItem(LOGOUT_FLAG_KEY);
  }, 1000);
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  return !!getAccessToken();
};

export const getLastActiveAt = () => {
  const raw = localStorage.getItem(LAST_ACTIVE_AT_KEY);
  const parsed = raw ? Number(raw) : 0;
  return Number.isFinite(parsed) ? parsed : 0;
};

export const isSessionInactive = () => {
  if (!isAuthenticated()) return false;
  const lastActiveAt = getLastActiveAt();
  if (!lastActiveAt) return false;
  return Date.now() - lastActiveAt > SESSION_IDLE_TIMEOUT_MS;
};

export const touchSessionActivity = () => {
  if (!isAuthenticated()) return 'anonymous';
  const now = Date.now();
  const lastActiveAt = getLastActiveAt();

  if (lastActiveAt && now - lastActiveAt > SESSION_IDLE_TIMEOUT_MS) {
    return 'expired';
  }

  if (!lastActiveAt || now - lastActiveAt >= SESSION_ACTIVITY_TOUCH_INTERVAL_MS) {
    localStorage.setItem(LAST_ACTIVE_AT_KEY, now.toString());
  }

  return 'active';
};

/**
 * Get authorization header for API calls
 */
export const getAuthHeader = () => {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Register new user
 */
export const register = async (email, password, name) => {
  const response = await fetch(`${getBackendUrl()}/api/auth/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password, name }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Registration failed');
  }

  const data = await response.json();
  setAuthTokens(data.access_token, data.refresh_token, data.user);
  return data;
};

/**
 * Login user
 */
export const login = async (email, password) => {
  const response = await fetch(`${getBackendUrl()}/api/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Login failed');
  }

  const data = await response.json();
  setAuthTokens(data.access_token, data.refresh_token, data.user);
  return data;
};

/**
 * Refresh access token
 */
export const refreshAccessToken = async () => {
  if (isSessionInactive()) {
    clearAuth();
    throw new Error('Session expired due to inactivity');
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await fetch(`${getBackendUrl()}/api/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!response.ok) {
    clearAuth();
    throw new Error('Token refresh failed');
  }

  const data = await response.json();
  setAuthTokens(data.access_token, data.refresh_token, data.user);
  return data;
};

// Track if we're currently refreshing to prevent infinite loops
let isRefreshing = false;
let refreshPromise = null;

const getTokenPayload = (token) => {
  try {
    const [, payload] = String(token || '').split('.');
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(normalized));
  } catch (_) {
    return null;
  }
};

const isAccessTokenExpiringSoon = (token, bufferSeconds = 90) => {
  const payload = getTokenPayload(token);
  if (!payload?.exp) return false;
  return payload.exp * 1000 <= Date.now() + bufferSeconds * 1000;
};

export const getFreshAuthHeader = async () => {
  if (isSessionInactive()) {
    clearAuth();
    throw new Error('Session expired due to inactivity');
  }

  let token = getAccessToken();
  const refreshToken = getRefreshToken();

  if ((!token && refreshToken) || (token && refreshToken && isAccessTokenExpiringSoon(token))) {
    if (isRefreshing && refreshPromise) {
      await refreshPromise;
    } else {
      isRefreshing = true;
      try {
        refreshPromise = refreshAccessToken();
        await refreshPromise;
      } finally {
        isRefreshing = false;
        refreshPromise = null;
      }
    }
    token = getAccessToken();
  }

  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ME_FETCH_MIN_INTERVAL_MS = 15000;
let meFetchInFlight = null;
let meLastFetchedAt = 0;

const readCachedUser = () => getUser();

const fetchMeFromNetwork = async (retryCount = 0) => {
  const token = getAccessToken();
  if (!token) return null;

  if (retryCount > 1) {
    console.error('Too many retries for getCurrentUser, clearing auth');
    clearAuth();
    return null;
  }

  try {
    const response = await fetch(`${getBackendUrl()}/api/auth/me`, {
      headers: { ...getAuthHeader() },
    });

    if (!response.ok) {
      if (response.status === 429) {
        const cached = readCachedUser();
        if (cached) return cached;
        return null;
      }
      if (response.status === 401 && retryCount === 0) {
        if (isRefreshing && refreshPromise) {
          await refreshPromise;
        } else {
          isRefreshing = true;
          try {
            refreshPromise = refreshAccessToken();
            await refreshPromise;
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            clearAuth();
            return null;
          } finally {
            isRefreshing = false;
            refreshPromise = null;
          }
        }
        await new Promise((resolve) => setTimeout(resolve, 200));
        if (!getAccessToken()) {
          clearAuth();
          return null;
        }
        return fetchMeFromNetwork(retryCount + 1);
      }
      if (response.status === 401) {
        clearAuth();
      }
      return readCachedUser();
    }

    const user = await response.json();
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    meLastFetchedAt = Date.now();
    return user;
  } catch (error) {
    console.error('Error fetching user:', error);
    if (retryCount > 0) clearAuth();
    return readCachedUser();
  }
};

/**
 * Get current user info. Dedupes concurrent calls and respects a short cache
 * window so refresh/boot does not hammer /api/auth/me (slowapi 20/min).
 */
export const getCurrentUser = async ({ force = false } = {}) => {
  const token = getAccessToken();
  if (!token) return null;

  const cached = readCachedUser();
  const cacheIsFresh = cached && (Date.now() - meLastFetchedAt) < ME_FETCH_MIN_INTERVAL_MS;
  if (!force && cacheIsFresh) return cached;

  if (!force && meFetchInFlight) {
    return meFetchInFlight;
  }

  meFetchInFlight = fetchMeFromNetwork()
    .finally(() => {
      meFetchInFlight = null;
    });

  return meFetchInFlight;
};

if (typeof window !== 'undefined' && readCachedUser()) {
  meLastFetchedAt = Date.now();
}

/**
 * Logout user
 */
export const logout = () => {
  clearAuth();
  meLastFetchedAt = 0;
};
